import { NotificationDto } from "@/hooks/services/workchestra/types/notification.type";
import { Notification } from "./types";

// NotificationTypes of the AeroStaff certification expiry notifications (WOR-2546).
export const CERTIFICATION_EXPIRY_EMPLOYEE_TYPE =
  "CertificationExpiryNotificationEvent";
export const CERTIFICATION_EXPIRY_QA_TYPE =
  "CertificationExpiryForQANotificationEvent";

// RelatedObjectName(s) that represent Library (Technical Manual) notifications.
export const libraryRelatedObjectNames = ["TechnicalManualBorrow"];

// Map a UI notification category to the backend RelatedObjectName(s) used for filtering.
// Returns undefined for "all" (no filter) and falls back to [category] for unknown values.
export function getCategoryRelatedObjectNames(
  category: string,
): string[] | undefined {
  const normalized = category.trim().toLowerCase().replace(/\s+/g, "");
  if (normalized === "all") return undefined;

  const categoryMap: Record<string, string[]> = {
    workchestra: ["Account", "JobOrder", "AuthoredFormVersions"],
    csr: ["ECSR"],
    ecsr: ["ECSR"],
    eforms: ["UserGenericForm"],
    aerostaff: ["AeroStaffCertification"],
    capmaster: ["CapMaster"],
    library: libraryRelatedObjectNames,
    technicalmanual: libraryRelatedObjectNames,
    technicalmanuals: libraryRelatedObjectNames,
    tool: ["IssuedToolList", "EquipmentCollection", "OutOfCalibrationCase"],
    tools: [
      "IssuedToolList",
      "MissingToolReport",
      "EquipmentCollection",
      "OutOfCalibrationCase",
    ],
    issuedtoollist: ["IssuedToolList"],
    audit: ["InternalAuditReport"],
    equipmentcollection: ["EquipmentCollection"],
    outofcalibrationcase: ["OutOfCalibrationCase"],
  };

  return categoryMap[normalized] || [category];
}

// Map a backend NotificationDto to the frontend Notification type, deriving the
// display label and owning app from the relatedObjectName.
export function mapNotificationDto(dto: NotificationDto): Notification {
  const labelMap: Record<string, string> = {
    account: "Account",
    usergenericform: "eForm",
    joborder: "Job Order",
    authoredformversions: "CRR",
    aerostaffcertification: "AeroTrak",
    capmaster: "Cap Master",
    technicalmanualborrow: "Library",
    internalauditreport: "Audit Report",
    issuedtoollist: "Tools",
    equipmentcollection: "Tools",
    outofcalibrationcase: "Out of Calibration",
  };

  const appMap: Record<string, string> = {
    aerostaffcertification: "AeroStaff",
    capmaster: "Cap Master",
    technicalmanualborrow: "Library",
    internalauditreport: "Audit",
    issuedtoollist: "Tool",
    equipmentcollection: "Tool",
    outofcalibrationcase: "Tool",
  };

  // The owner's link opens AeroTrak My Profile; QA's opens the AeroStaff employee page.
  const actionTextByType: Record<string, string> = {
    [CERTIFICATION_EXPIRY_EMPLOYEE_TYPE]: "View My Profile",
    [CERTIFICATION_EXPIRY_QA_TYPE]: "View Employee Certification",
  };
  const appByType: Record<string, string> = {
    [CERTIFICATION_EXPIRY_EMPLOYEE_TYPE]: "AeroTrak",
  };

  const relatedObjectName = dto.relatedObjectName?.toLowerCase() || "";
  const notificationType = dto.notificationType ?? "";

  return {
    id: dto.id,
    title: dto.title,
    description: dto.message,
    notificationType: dto.notificationType,
    actionText: actionTextByType[notificationType] || "View Details",
    actionLink: dto.redirectUrl || "#",
    label: labelMap[relatedObjectName] || dto.relatedObjectName || "",
    isRead: dto.isRead,
    createdAt: new Date(dto.createdOn),
    app: appByType[notificationType] || appMap[relatedObjectName] || "Other",
  };
}
